// src/theme/kioskDesignTokens.js
//
// Builds the MUI theme options for the kiosk from the store owner's saved
// `kioskConfig.kioskTheme` overrides.
//
// TKT-2026-104509. The kiosk UI was styled with hardcoded colours in Oct 2025
// ("chore: upgraded ui"), and the owner-configurable palette was layered on top
// of it in Feb 2026 without removing any of them. Several fields on
// Integrations > Kiosk Configuration > Theme Customization therefore had little
// or no effect on the rendered kiosk:
//
//   1. Card / Paper Color was painted over by a fixed 0.88-0.96 alpha gradient,
//      so cards rendered stock white (light) or stock charcoal (dark) whatever
//      the owner chose. Combined with a custom Primary Text Color, that is how
//      white-on-white happened.
//   2. contrastText for primary / secondary / success was pinned to white, and
//      the AppBar and filled Chips re-pinned white on top of that, so a pale
//      Primary or Success colour produced unreadable labels.
//   3. Contained-primary hover, the button/AppBar shadows and the Paper border
//      were pinned to the stock SortSwift blue, so hovering a button snapped it
//      away from the store's own primary colour.
//   4. primary.light / primary.dark / secondary.* were fixed blues, which MUI
//      uses for focus, outline and hover states across the whole kiosk.
//   5. Text colours left empty defaulted off the page background rather than
//      off the card the text actually sits on.
//   6. palette.mode came only from the kiosk device's OS preference, so a
//      hand-built dark palette landed on a light-mode MUI base whenever the
//      device was in light mode, and every derived token fought the palette.
//
// TKT-2026-105169 then found the half of the same bug that 104509 missed. The
// Primary Color is painted two ways, as a FILL (AppBar, contained Button,
// filled Chip) and as a FOREGROUND on a card or the page (every money figure,
// the cart stepper, outlined Buttons, spinners, focus rings). 104509 made the
// label on the fill readable; nothing made the foreground readable against the
// store's own card colour, so a near-black primary on a near-black card
// rendered at 1.0:1. `palette.primary.onSurface` is that second role, split out
// and contrast corrected. See `readableForeground` below.
//
// EVERY derived value below is gated on the owner having actually set that
// field. A store with no overrides saved gets byte-identical output to before
// this change, which `test 0` in the reproduction suite pins down. We fix the
// colours the owner chose being ignored; we do not restyle anyone else's kiosk.
//
// TKT-2026-107229 (kiosk sibling of the buylist fix). Every colour below goes
// through MUI's colour maths, which throws on a value it cannot parse (`ffffff`
// with no '#', `RGB(1,2,3)`, `red`), and the store's colours used to arrive
// verbatim from a free-text box. resolveMode and getDesignTokens therefore
// normalise the overrides on entry (utils/themeColour: a bare hex gains its
// '#', anything unparseable falls back to stock), and App.js builds the theme
// through buildKioskTheme, which falls back to the stock theme if building the
// store's still throws.
import { createTheme } from '@mui/material';
import { alpha, darken, getContrastRatio, getLuminance, lighten } from '@mui/material/styles';
import { normaliseKioskTheme } from '../utils/themeColour';

const defaultDarkShadows = createTheme({ palette: { mode: 'dark' } }).shadows;

// The stock kiosk look, exactly as it shipped. Used verbatim for every field
// the owner has not overridden.
const STOCK = {
    light: {
        primary: '#3EACFD',
        secondary: '#3E6AFD',
        success: '#4CAF50',
        background: '#FAFBFF',
        paper: '#FAFBFF',
        textPrimary: '#000000',
        textSecondary: '#6E7180',
        paperGradient: 'linear-gradient(145deg, rgba(255,255,255,0.96), rgba(237,239,247,0.9))',
        paperBorder: '1px solid rgba(62,172,253,0.12)',
    },
    dark: {
        primary: '#3EACFD',
        secondary: '#3E6AFD',
        success: '#4CAF50',
        background: '#000000',
        paper: '#1E1E24',
        textPrimary: '#FAFBFF',
        textSecondary: '#9DA2B3',
        paperGradient: 'linear-gradient(145deg, rgba(30,30,36,0.95), rgba(64,66,77,0.88))',
        paperBorder: '1px solid rgba(157,162,179,0.16)',
    },
};

const STOCK_PRIMARY_LIGHT = '#7FC8FD';
const STOCK_PRIMARY_DARK = '#2E8ACA';
const STOCK_SECONDARY_LIGHT = '#7FA3FD';
const STOCK_SECONDARY_DARK = '#2E51CA';
const STOCK_SUCCESS_LIGHT = '#81C784';
const STOCK_SUCCESS_DARK = '#388E3C';
const STOCK_ON_BRAND = '#FAFBFF';
const STOCK_ON_SUCCESS = '#FFFFFF';
const STOCK_APPBAR_SHADOW = '0 14px 28px rgba(62,172,253,0.18)';
const STOCK_BUTTON_SHADOW = '0 12px 24px rgba(62,172,253,0.24)';
const STOCK_BUTTON_SHADOW_HOVER = '0 16px 32px rgba(62,172,253,0.32)';
const STOCK_SHADOW_2 = '0px 4px 12px rgba(62, 172, 253, 0.12)';
const STOCK_SHADOW_3 = '0px 10px 30px rgba(62, 106, 253, 0.14)';

const ON_DARK = '#FFFFFF';
const ON_LIGHT = '#12141A';

// The header's selected-toggle pill, which is painted white regardless of the
// store's palette. TKT-2026-105169.
const WHITE_SURFACE = '#FFFFFF';

/** WCAG AA for body text. */
const AA_TEXT = 4.5;

/** A saved override only counts when it is a real value, not '' or null. */
const has = (v) => typeof v === 'string' && v.trim() !== '';

/** Treat a colour as dark when it wants light text on top of it. */
const isDarkColor = (color) => getLuminance(color) < 0.35;

/**
 * Foreground that is actually readable on `bg`. Prefers white for the brand
 * look, falls back to near-black when white would not clear WCAG AA.
 */
function readableOn(bg) {
    return getContrastRatio(bg, ON_DARK) >= AA_TEXT ? ON_DARK : ON_LIGHT;
}

/**
 * TKT-2026-105169. The store's Primary Color is asked to do two incompatible
 * jobs and only one of them was ever contrast checked.
 *
 *   FILL        the AppBar, contained Buttons and filled Chips paint primary as
 *               a background. `contrastText` is the readable partner for that,
 *               which is what TKT-2026-104509 fixed.
 *   FOREGROUND  everything else paints primary ON a card or on the page: every
 *               money figure, the cart quantity stepper, the empty-cart icon,
 *               outlined and text Buttons, outlined Chips, spinners, Switches
 *               and the focused input outline.
 *
 * Nothing checked the second pairing, so d20dungeon setting Primary Color AND
 * Card Color to #060c10 rendered that entire set at a 1.0 contrast ratio. Their
 * only escape was a pale primary, which then washed out the header, so no
 * combination worked. `palette.primary.onSurface` is that second role, split
 * out and made readable; `palette.primary.main` still holds their colour
 * exactly as configured, so every fill keeps rendering it.
 *
 * Returns `color` untouched when it already clears AA, so a store whose primary
 * reads fine against its own surfaces sees no change at all.
 */
function readableForeground(color, surfaces, fallback) {
    const clearsAll = (c) => surfaces.every((s) => getContrastRatio(c, s) >= AA_TEXT);
    const clearsCards = (c) => getContrastRatio(c, surfaces[0]) >= AA_TEXT;

    if (clearsAll(color)) return color;

    // Walk the store's own hue away from their surfaces, both directions, and
    // stop at halfway. Past that the result is no longer recognisably their
    // colour, and the text colour they already chose is the better answer than
    // a grey we invented.
    let cardsOnly = null;
    for (let step = 5; step <= 50; step += 5) {
        for (const candidate of [lighten(color, step / 100), darken(color, step / 100)]) {
            if (clearsAll(candidate)) return candidate;
            if (!cardsOnly && clearsCards(candidate)) cardsOnly = candidate;
        }
    }

    // A dark page with light cards (or the reverse) has no single readable
    // accent. Cards win: that is where the prices and the controls live.
    if (clearsAll(fallback)) return fallback;
    if (cardsOnly) return cardsOnly;
    if (clearsCards(fallback)) return fallback;

    // Nothing clears. A store whose own body text is also below AA on its cards
    // (mid-grey surfaces cannot take black or white) is a palette the admin
    // Readability check already flags. Leave their colour alone rather than
    // paint something they never chose.
    return color;
}

/**
 * Which appearance the kiosk renders in, highest priority first (TKT-2026-104509):
 *
 *   1. `shopperMode`  the sun/moon button in the kiosk header. A shopper who has
 *                     explicitly asked for light or dark always gets it. This tier
 *                     exists because deriving the mode from the palette alone made
 *                     that button inert for every store with a configured
 *                     background or card colour.
 *   2. `defaultColorMode` 'light' | 'dark'  the owner's stated intent.
 *   3. palette luminance   the owner configured colours but no mode, so match the
 *                     appearance to the colours they chose. Without this a dark
 *                     palette lands on a light-mode MUI base and every derived
 *                     token (dividers, disabled text, outlines, overlays) fights
 *                     the palette.
 *   4. `deviceMode`   the device's own preference. Also what `defaultColorMode:
 *                     'system'` selects, which is the owner explicitly opting OUT
 *                     of tier 3. That can put a dark palette on a light base, so
 *                     the admin readability panel warns about it rather than
 *                     silently overriding the choice.
 *
 * '' and undefined are the same thing at tier 2: untouched, fall through.
 */
export function resolveMode(deviceMode, rawOverrides = {}, shopperMode = null) {
    const overrides = normaliseKioskTheme(rawOverrides || {});
    const device = deviceMode === 'light' ? 'light' : 'dark';

    if (shopperMode === 'light' || shopperMode === 'dark') return shopperMode;

    const owner = overrides.defaultColorMode;
    if (owner === 'light' || owner === 'dark') return owner;
    if (owner === 'system') return device;

    const anchor = has(overrides.backgroundColor)
        ? overrides.backgroundColor
        : has(overrides.paperColor)
            ? overrides.paperColor
            : null;
    if (!anchor) return device;
    return isDarkColor(anchor) ? 'dark' : 'light';
}

/**
 * The soft 145deg sheen kiosk cards have always had, rebuilt from the owner's
 * paper colour so their choice survives it. Only used when they set one.
 */
function paperGradient(paper) {
    return isDarkColor(paper)
        ? `linear-gradient(145deg, ${paper}, ${lighten(paper, 0.16)})`
        : `linear-gradient(145deg, ${lighten(paper, 0.05)}, ${darken(paper, 0.045)})`;
}

/**
 * The page wash behind the cards, rebuilt from the owner's background colour.
 * Only used when they set one.
 */
function pageGradient(background) {
    return isDarkColor(background)
        ? `linear-gradient(180deg, ${darken(background, 0.35)} 0%, ${background} 65%, ${darken(background, 0.35)} 100%)`
        : `linear-gradient(180deg, ${lighten(background, 0.35)} 0%, ${background} 60%, ${darken(background, 0.06)} 100%)`;
}

export const getDesignTokens = (deviceMode, rawOverrides = {}, shopperMode = null) => {
    const overrides = normaliseKioskTheme(rawOverrides || {});
    const mode = resolveMode(deviceMode, overrides, shopperMode);
    const isLight = mode === 'light';
    const stock = STOCK[mode];

    const hasPrimary = has(overrides.primaryColor);
    const hasSecondary = has(overrides.secondaryColor);
    const hasSuccess = has(overrides.successColor);
    const hasPaper = has(overrides.paperColor);
    const hasBackground = has(overrides.backgroundColor);

    const primaryMain = hasPrimary ? overrides.primaryColor : stock.primary;
    const secondaryMain = hasSecondary ? overrides.secondaryColor : stock.secondary;
    const successMain = hasSuccess ? overrides.successColor : stock.success;
    const bgDefault = hasBackground ? overrides.backgroundColor : stock.background;
    const bgPaper = hasPaper ? overrides.paperColor : stock.paper;

    // Text defaults follow the CARD the text is painted on, not the page behind
    // it. A store with a dark page background and light cards would otherwise
    // inherit the dark-mode near-white default and render near-white body text
    // on a light card. Stores that set no colours are unaffected: their paper
    // already matches their mode, so these resolve to the stock values. An
    // explicitly chosen colour is always used verbatim.
    const paperTone = isDarkColor(bgPaper) ? STOCK.dark : STOCK.light;
    const txtPrimary = has(overrides.textPrimary) ? overrides.textPrimary : paperTone.textPrimary;
    const txtSecondary = has(overrides.textSecondary) ? overrides.textSecondary : paperTone.textSecondary;

    const fontFamily = has(overrides.fontFamily)
        ? `'${overrides.fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`
        : `'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;

    // The FOREGROUND half of the store's primary colour (TKT-2026-105169). Cards
    // are listed first: they are the surface the prices and the controls sit on.
    const primaryOnSurface = hasPrimary
        ? readableForeground(primaryMain, [bgPaper, bgDefault], txtPrimary)
        : stock.primary;
    // Only true for a store whose Primary Color is unreadable on its own
    // surfaces. Every override below is gated on it, so a store that is already
    // legible, and every store with nothing configured, is byte-identical.
    const needsAccent = primaryOnSurface !== primaryMain;

    // The Singles/Sealed and Buy/Sell toggles in the header paint their selected
    // label in the primary colour on a hardcoded WHITE pill, which is neither
    // the card colour nor the page. A bright primary reads at 1.4:1 there, so
    // that one surface gets its own derivation. Stores with nothing configured
    // keep the stock blue they have today rather than gaining a restyle.
    const primaryOnLight = hasPrimary
        ? readableForeground(primaryMain, [WHITE_SURFACE], ON_LIGHT)
        : primaryMain;

    // Foregrounds: derived only where the owner supplied the background colour
    // they sit on, so the stock kiosk keeps its existing white-on-blue look.
    const onPrimary = hasPrimary ? readableOn(primaryMain) : STOCK_ON_BRAND;
    const onSecondary = hasSecondary ? readableOn(secondaryMain) : STOCK_ON_BRAND;
    const onSuccess = hasSuccess ? readableOn(successMain) : STOCK_ON_SUCCESS;

    const primaryLight = hasPrimary ? lighten(primaryMain, 0.35) : STOCK_PRIMARY_LIGHT;
    const primaryDark = hasPrimary ? darken(primaryMain, 0.25) : STOCK_PRIMARY_DARK;
    const primaryHover = hasPrimary
        ? (isDarkColor(primaryMain) ? lighten(primaryMain, 0.18) : darken(primaryMain, 0.18))
        : STOCK_PRIMARY_DARK;

    const appBarShadow = hasPrimary ? `0 14px 28px ${alpha(primaryMain, 0.18)}` : STOCK_APPBAR_SHADOW;
    const buttonShadow = hasPrimary ? `0 12px 24px ${alpha(primaryMain, 0.24)}` : STOCK_BUTTON_SHADOW;
    const buttonShadowHover = hasPrimary
        ? `0 16px 32px ${alpha(primaryMain, 0.32)}`
        : STOCK_BUTTON_SHADOW_HOVER;

    // TKT-2026-105169, second pass. The theme has always rebuilt the card sheen
    // from the owner's Card Color, but only for a bare <Paper>. Every card,
    // panel, dialog, drawer and page background the shopper actually looks at
    // paints its own `background:` in an sx block, hardcoded to the stock light
    // or stock dark gradient and chosen off `palette.mode`. sx wins on
    // specificity, so those 20 surfaces have never rendered the store's colours
    // at all. In dark mode the stock charcoal is close enough to pass; in light
    // mode a store with a dark palette gets stock WHITE cards under the light
    // text colour they chose for their own dark cards, which is the
    // white-on-white d20dungeon reported. Six of the nine stores with a surface
    // colour saved are below AA in light mode today for exactly this reason,
    // and one is below AA in dark.
    //
    // These three are null unless the owner set the colour they are built from,
    // so every site reads `palette.background.ownerCard || <the stock ternary it
    // already had>` and a store with nothing configured stays byte-identical.
    const ownerCard = hasPaper ? paperGradient(bgPaper) : null;
    const ownerPage = hasBackground ? pageGradient(bgDefault) : null;
    // Panels and fields lift themselves off their surface with a translucent
    // white wash, at 0.92 on a light surface and 0.12 on a dark one, picked off
    // `palette.mode`. The tone that matters is the SURFACE the wash sits on, not
    // the mode, so expose that instead. Without a Card Color the two agree.
    const surfaceIsDark = hasPaper ? isDarkColor(bgPaper) : mode === 'dark';

    const lightShadows = [
        'none',
        '0px 1px 2px rgba(0, 0, 0, 0.08)',
        hasPrimary ? `0px 4px 12px ${alpha(primaryMain, 0.12)}` : STOCK_SHADOW_2,
        hasSecondary ? `0px 10px 30px ${alpha(secondaryMain, 0.14)}` : STOCK_SHADOW_3,
        ...Array(21).fill('0px 16px 40px rgba(0, 0, 0, 0.08)'),
    ];

    return {
        palette: {
            mode,
            primary: {
                main: primaryMain,
                light: primaryLight,
                dark: primaryDark,
                contrastText: onPrimary,
                // The same colour, made readable AS text on the store's own
                // cards and page. Anything painting primary as a foreground
                // rather than as a fill reads this. TKT-2026-105169.
                onSurface: primaryOnSurface,
                // And on the white toggle pill in the header, which is the one
                // surface that is not the store's own colour.
                onLight: primaryOnLight,
                // True only for a store whose Primary Color is unreadable on its
                // own surfaces. Components that hand-roll a foreground shade of
                // primary read this so every other store keeps exactly the shade
                // it has today.
                needsAccent,
            },
            secondary: {
                main: secondaryMain,
                light: hasSecondary ? lighten(secondaryMain, 0.35) : STOCK_SECONDARY_LIGHT,
                dark: hasSecondary ? darken(secondaryMain, 0.25) : STOCK_SECONDARY_DARK,
                contrastText: onSecondary,
            },
            success: {
                main: successMain,
                light: hasSuccess ? lighten(successMain, 0.3) : STOCK_SUCCESS_LIGHT,
                dark: hasSuccess ? darken(successMain, 0.25) : STOCK_SUCCESS_DARK,
                contrastText: onSuccess,
            },
            warning: { main: '#F59E0B', dark: '#B45309' },
            error: { main: '#EF4444', dark: '#B91C1C' },
            background: {
                default: bgDefault,
                paper: bgPaper,
                // The store's own surfaces, for the components that paint their
                // own background instead of inheriting Paper's. Null when the
                // owner set no colour to build them from. TKT-2026-105169.
                ownerCard,
                ownerPage,
            },
            // True when the surface the kiosk actually paints is dark, which is
            // the store's Card Color when they set one and the mode otherwise.
            // TKT-2026-105169.
            surfaceIsDark,
            text: {
                primary: txtPrimary,
                secondary: txtSecondary,
            },
        },
        typography: {
            fontFamily,
            h1: { fontWeight: 700, letterSpacing: '-0.015em' },
            h2: { fontWeight: 700, letterSpacing: '-0.01em' },
            h3: { fontWeight: 600 },
            h4: { fontWeight: 600 },
            h5: { fontWeight: 600 },
            h6: { fontWeight: 600 },
            subtitle1: { fontWeight: 500 },
            button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.01em' },
        },
        shape: { borderRadius: 12 },
        shadows: isLight ? lightShadows : defaultDarkShadows,
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    'html, body': {
                        backgroundColor: bgDefault,
                    },
                    body: {
                        backgroundImage: 'none',
                    },
                },
            },
            MuiPaper: {
                defaultProps: { elevation: 0 },
                styleOverrides: {
                    root: {
                        borderRadius: 14,
                        backgroundImage: hasPaper ? paperGradient(bgPaper) : stock.paperGradient,
                        // No backdropFilter here. Every product card in the grid is a
                        // Paper, so a blur on this root put one GPU blur pass per card
                        // on every scroll frame (206 blurred surfaces at 100 cards),
                        // which is what made scrolling stutter on budget Android
                        // tablets while the storefront scrolled smoothly. It painted
                        // nothing: Paper's own background is opaque, and the cards sit
                        // at 94%+ over a smooth page gradient. The surfaces that float
                        // over other content keep it below, one each. TKT-2026-107279.
                        border: hasPaper ? `1px solid ${alpha(txtPrimary, 0.12)}` : stock.paperBorder,
                    },
                },
            },
            // Overlays: a single surface over the page, so the blur is one pass
            // and the faint page behind them stays frosted exactly as before.
            // (MuiDialog's paper keeps it too, in its entry at the bottom.)
            MuiPopover: { styleOverrides: { paper: { backdropFilter: 'blur(16px)' } } },
            MuiDrawer: { styleOverrides: { paper: { backdropFilter: 'blur(16px)' } } },
            MuiAutocomplete: { styleOverrides: { paper: { backdropFilter: 'blur(16px)' } } },
            MuiChip: {
                styleOverrides: {
                    root: {
                        fontWeight: 600,
                    },
                    filled: {
                        '&.MuiChip-colorPrimary': {
                            backgroundColor: primaryMain,
                            color: hasPrimary ? onPrimary : '#FFFFFF',
                        },
                    },
                    // An outlined Chip paints primary as its label and border,
                    // not as a fill. TKT-2026-105169.
                    ...(needsAccent && {
                        outlinedPrimary: {
                            color: primaryOnSurface,
                            borderColor: alpha(primaryOnSurface, 0.5),
                        },
                    }),
                },
            },
            // Everything below is MUI painting primary AS a foreground on a card
            // or on the page, which is only readable once primaryOnSurface
            // exists. All of it is gated on needsAccent, so a store whose colour
            // already reads gets none of these rules. TKT-2026-105169.
            ...(needsAccent && {
                MuiIconButton: {
                    styleOverrides: { colorPrimary: { color: primaryOnSurface } },
                },
                MuiSvgIcon: {
                    styleOverrides: { colorPrimary: { color: primaryOnSurface } },
                },
                MuiCircularProgress: {
                    styleOverrides: { colorPrimary: { color: primaryOnSurface } },
                },
                MuiLink: {
                    styleOverrides: { root: { color: primaryOnSurface } },
                },
                MuiCheckbox: {
                    styleOverrides: { colorPrimary: { '&.Mui-checked': { color: primaryOnSurface } } },
                },
                MuiRadio: {
                    styleOverrides: { colorPrimary: { '&.Mui-checked': { color: primaryOnSurface } } },
                },
                MuiSwitch: {
                    styleOverrides: {
                        colorPrimary: {
                            '&.Mui-checked': { color: primaryOnSurface },
                            '&.Mui-checked + .MuiSwitch-track': { backgroundColor: primaryOnSurface },
                        },
                    },
                },
                // A field the shopper is typing into must show where the caret
                // is. Both of these are primary.main by default.
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: primaryOnSurface,
                            },
                        },
                    },
                },
                MuiFormLabel: {
                    styleOverrides: { root: { '&.Mui-focused': { color: primaryOnSurface } } },
                },
            }),
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        borderRadius: 0,
                        margin: 0,
                        width: '100%',
                        padding: '6px 16px',
                        backgroundColor: primaryMain,
                        backgroundImage: 'none',
                        boxShadow: appBarShadow,
                        color: hasPrimary ? onPrimary : '#FFFFFF',
                        '& .MuiTypography-root': {
                            color: hasPrimary ? onPrimary : '#FFFFFF',
                        },
                        '& .MuiIconButton-root': {
                            color: hasPrimary ? onPrimary : '#FFFFFF',
                        },
                        '& .MuiChip-root': {
                            color: hasPrimary ? onPrimary : '#FFFFFF',
                        },
                    },
                },
            },
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                        paddingTop: 9,
                        paddingBottom: 9,
                        paddingLeft: 18,
                        paddingRight: 18,
                    },
                    containedPrimary: {
                        backgroundColor: primaryMain,
                        backgroundImage: 'none',
                        boxShadow: buttonShadow,
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                            backgroundColor: primaryHover,
                            transform: 'translateY(-2px)',
                            boxShadow: buttonShadowHover,
                        },
                    },
                    // Outlined and text Buttons paint primary as their label and
                    // border, not as a fill. The checkout email shortcuts are
                    // outlined, which is why they vanished. TKT-2026-105169.
                    ...(needsAccent && {
                        outlinedPrimary: {
                            color: primaryOnSurface,
                            borderColor: alpha(primaryOnSurface, 0.5),
                            '&:hover': {
                                borderColor: primaryOnSurface,
                                backgroundColor: alpha(primaryOnSurface, 0.08),
                            },
                        },
                        textPrimary: {
                            color: primaryOnSurface,
                            '&:hover': { backgroundColor: alpha(primaryOnSurface, 0.08) },
                        },
                    }),
                },
            },
            MuiListItem: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        marginBottom: 4,
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        fontSize: '0.85rem',
                        borderRadius: 8,
                        padding: '8px 12px',
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: 16,
                        // An overlay over the page, so it keeps the blur MuiPaper
                        // root no longer gives every Paper. TKT-2026-107279.
                        backdropFilter: 'blur(16px)',
                    },
                },
            },
        },
    };
};

// Each distinct failure is logged once, not on every re-render; capped so a
// long-running kiosk cannot grow the set without bound. console.error is
// forwarded to the error tracker by App.js.
const loggedThemeFailures = new Set();
function logThemeFailureOnce(err, overrides) {
    const message = (err && err.message) || String(err);
    if (loggedThemeFailures.has(message) || loggedThemeFailures.size >= 100) return;
    loggedThemeFailures.add(message);
    let saved = '';
    try {
        saved = JSON.stringify(overrides);
    } catch {
        saved = '[unserialisable]';
    }
    // eslint-disable-next-line no-console
    console.error(`Kiosk theme: the store's saved theme could not be built, using the stock theme. ${message} ${saved}`);
}

/**
 * The MUI theme and the mode it renders in, for App.js. Building the store's
 * own theme must never take the kiosk down (TKT-2026-107229): if it throws for
 * any reason the kiosk renders the stock theme for the same device and shopper
 * choice, exactly as a store that has saved no colours does.
 */
export function buildKioskTheme(deviceMode, overrides, shopperMode = null) {
    try {
        return {
            mode: resolveMode(deviceMode, overrides || {}, shopperMode),
            theme: createTheme(getDesignTokens(deviceMode, overrides || {}, shopperMode)),
        };
    } catch (err) {
        logThemeFailureOnce(err, overrides);
        return {
            mode: resolveMode(deviceMode, {}, shopperMode),
            theme: createTheme(getDesignTokens(deviceMode, {}, shopperMode)),
        };
    }
}

export default getDesignTokens;
